import { useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import { useSettingContext } from '@context/SettingContent';
import { CustomDropdown, Input, Loader } from '@components';
import { providerLabel } from '@helpers/setting';
import { registerUser } from '@apis/Auth';
import Link from 'next/link';
import { useMessageContext } from '@context/MessageContext';
import style from './psp.module.scss';
import styles from '../../../styles/registration.module.scss';
import getClientConfigByName from '../../../helpers/theme-values-provider';

const SEARCH_ICON = '/images/search-icon.svg';
const WARNING_ICON = '/images/warning-red.svg';

const Register = () => {
  const router = useRouter();
  const { clientName = 'payment-loyalty' } = router.query;
  const clientConfig = getClientConfigByName(clientName.replace(/-/g, '').toLowerCase());
  const { metaData } = useSettingContext();
  const { contextSuccessHandler } = useMessageContext();

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
    businessName: '',
    phoneNumber: '',
    businessType: null,
    country: null,
    psp: {},
  });

  const {
    firstName,
    lastName,
    email,
    password,
    confirmPassword,
    businessName,
    phoneNumber,
    businessType,
    country,
    psp,
  } = formData;

  const [isValidClient, setValidClient] = useState(false);
  // psp related variables
  const [searchPsp, handleSearchPsp] = useState('');
  const [selectPsp, handleSelectPsp] = useState(false);
  // for form submission
  const [hasError, handleHasError] = useState({});
  const [canSubmit, handleCanSubmit] = useState(false);
  const [isLoading, setLoading] = useState(false);
  const [registerStatus, handleRegisterStatus] = useState({
    status: true,
    message: 'Something went wrong',
  });
  const { status, message } = registerStatus;

  useEffect(() => {
    const formattedClientName = clientName.replace(/-/g, '').toLowerCase();
    const pspItem = metaData?.service_provider.find(
      item => item.label.toLowerCase() === formattedClientName,
    );
    if (pspItem) {
      setValidClient(true);
      handleSelected(pspItem);
    } else {
      setValidClient(false);
    }
  }, [clientName, metaData]);

  const handleSubmit = async e => {
    e.preventDefault();
    if (selectPsp) return; // to avoid submitting form
    // prepare user object
    const body = {
      email,
      password,
      phone: phoneNumber,
      firstName,
      lastName,
      businessName,
      merchantNumber: '0000',
      provider: +psp.id,
      businessType: +businessType.value,
      country: +country.value,
      deviceId: clientConfig.deviceId,
    };

    const isValid = validateBody(body);

    if (isValid) {
      if (password !== confirmPassword) {
        handleRegisterStatus({
          status: false,
          message: 'Passwords do not match. Please ensure both password fields are identical.',
        });
        return;
      }

      setLoading(true);
      const { status, message } = await registerUser(body);
      setLoading(false);
      if (status) {
        contextSuccessHandler(
          'Your account has been made, you will get account approval confirmation on the email',
        );
        router.replace(`/${clientName}`);
      } else {
        handleRegisterStatus({ status, message });
      }
    } else {
      handleRegisterStatus({ status: false, message: 'Please provide all required details!' });
    }
  };

  function validateBody(body) {
    return !Object.values(body).some(value => value === '' || value == null);
  }

  function updateField(name, value) {
    handleCanSubmit(false);
    setFormData({
      ...formData,
      [name]: value.trim(),
    });
  }

  function onSelectChange(key, value) {
    handleCanSubmit(false);
    setFormData(state => ({ ...state, [key]: value }));
  }

  function handleError({ name, value }) {
    const errors = { ...hasError };
    if (value) {
      errors[name] = 'error';
    } else {
      delete errors[name];
    }

    handleHasError(errors);
  }

  function getBusiness() {
    return metaData.business_type.reduce(
      (arr, data) =>
        arr.concat({
          label: data.label,
          value: data.id,
        }),
      [],
    );
  }

  function getCountries() {
    return metaData.country.reduce(
      (arr, data) =>
        arr.concat({
          label: data.label,
          value: data.id,
        }),
      [],
    );
  }

  function comparePsp(a, b) {
    return a.label.localeCompare(b.label);
  }

  function handleSelected(psp) {
    handleCanSubmit(false);
    handleSelectPsp(false);
    setFormData(state => ({ ...state, psp }));
  }

  return (
    <>
      {selectPsp ? (
        <section className={style['modal-wrap']}>
          <div className={style['modal-card']}>
            <div className={style['modal-header']}>
              <h3>Select Payment Service Provider</h3>
              <button
                type="button"
                className={style['close-icon']}
                onClick={() => handleSelectPsp(false)}
              >
                ✕
              </button>
            </div>
            <div className={style.search}>
              <input
                type="text"
                placeholder="Search providers..."
                value={searchPsp}
                onChange={e => handleSearchPsp(e.target.value)}
              />
              <span>
                <img src={SEARCH_ICON} alt="search-icon" />
              </span>
            </div>
            <div className={style['psp-list']}>
              {metaData?.service_provider?.sort(comparePsp).map(psp => {
                if (searchPsp && !psp.label.toLowerCase().includes(searchPsp.toLowerCase())) {
                  return null;
                }
                return (
                  <button
                    key={psp.id}
                    type="button"
                    className={style['psp-item']}
                    onClick={() => handleSelected(psp)}
                  >
                    <span className={style['psp-img']}>{providerLabel(psp) || ''}</span>
                    <span>{psp.label || ''}</span>
                  </button>
                );
              })}
            </div>
          </div>
        </section>
      ) : (
        ''
      )}

      <div className={styles.container} style={{ backgroundColor: clientConfig.background }}>
        <img src={clientConfig.logo} alt="Logo Payment Loyalty" className={styles.logo} />
        <h1 className={styles.title} style={{ color: clientConfig.onBackground }}>
          Enter a few details and we&#39;ll get you set-up!
        </h1>
        <form onSubmit={handleSubmit} className={styles.registrationForm}>
          {!status && (
            <div className="alert alert-danger" role="alert">
              {message}
            </div>
          )}
          <div className={styles.formGroup}>
            <Input
              title="First name"
              type="text"
              name="firstName"
              placeholder="First name"
              icon="/images/ic_user.svg"
              value={firstName}
              onChange={value => updateField('firstName', value)}
            />
            <Input
              title="Last name"
              type="text"
              name="lastName"
              placeholder="Last name"
              icon="/images/ic_user.svg"
              value={lastName}
              onChange={value => updateField('lastName', value)}
            />
          </div>
          <div className={styles.formGroup}>
            <Input
              title="Phone number"
              type="tel"
              name="phone"
              placeholder="Phone Number"
              icon="/images/ic_phone.svg"
              initialValue={phoneNumber}
              validations={[
                {
                  validation: /^.{1,}/i,
                  message: 'Please enterPhone Number',
                },
                {
                  validation: /^.{7,}/i,
                  message: 'Invalid Number',
                },
              ]}
              onChange={value => updateField('phoneNumber', value)}
              onError={errorValue => handleError(errorValue)}
            />
            <Input
              title="Email"
              type="email"
              name="email"
              placeholder="Email"
              icon="/images/ic_email.svg"
              value={email}
              onChange={value => updateField('email', value)}
            />
          </div>
          <div className={styles.formGroup}>
            <Input
              title="Password"
              type="password"
              name="password"
              initialValue={password}
              placeholder="Choose a Password"
              validations={[
                {
                  validation: /^.{1,}/i,
                  message: 'Please enter password',
                },
                {
                  validation: /^.{8,}/i,
                  message: 'Please enter a valid password it must contains 8 characters',
                },
              ]}
              onChange={value => updateField('password', value)}
              onError={errorValue => handleError(errorValue)}
            />
            <Input
              title="Confirm password"
              type="password"
              name="confirmPassword"
              placeholder="Confirm Password"
              value={confirmPassword}
              validations={[
                {
                  validation: /^.{1,}/i,
                  message: 'Please enter password',
                },
                {
                  validation: /^.{8,}/i,
                  message: 'Please enter a valid password it must contains 8 characters',
                },
              ]}
              onChange={value => updateField('confirmPassword', value)}
              onError={errorValue => handleError(errorValue)}
            />
          </div>
          <div className={styles.formGroup}>
            <Input
              title="Business name"
              type="text"
              name="businessName"
              placeholder="Business Name"
              icon="/images/ic_business.svg"
              value={businessName}
              onChange={value => updateField('businessName', value)}
            />
            <div className={styles.field}>
              <span className={styles.fieldLabel}>Business type</span>
              <CustomDropdown
                className="auth-select"
                name="businessType"
                initialValue={businessType}
                placeholder="Business Type"
                onChange={value => onSelectChange('businessType', value)}
                onError={errorValue => handleError(errorValue)}
                getOption={getBusiness()}
                customStyle={{ color: clientConfig.primary }}
              />
            </div>
          </div>
          <div className={styles.formGroup}>
            <div className={styles.field}>
              <span className={styles.fieldLabel}>Country</span>
              <CustomDropdown
                className="auth-select"
                name="country"
                initialValue={country}
                placeholder={
                  isValidClient
                    ? 'Select Country (To enable transactions in the local currency)'
                    : 'Select Country'
                }
                onChange={value => onSelectChange('country', value)}
                onError={errorValue => handleError(errorValue)}
                getOption={getCountries()}
                customStyle={{ color: clientConfig.primary }}
              />
            </div>
            {!isValidClient ? (
              <div className={styles.field}>
                <span className={styles.fieldLabel}>Payment service provider</span>
                <div className={styles.pspSelector}>
                  <button onClick={() => handleSelectPsp(true)}>
                    {psp?.label ? (
                      <span className={styles.selectedProvider}>
                        <span className={styles.providerIcon}>{providerLabel(psp)}</span>
                        <span className={styles.selectedText}>{psp?.label}</span>
                      </span>
                    ) : (
                      <>
                        <span className={styles.placeholderText}>
                          Your Payment Service Provider
                        </span>
                        <img className={styles.searchIcon} src={SEARCH_ICON} alt="search-icon" />
                      </>
                    )}
                  </button>
                  {canSubmit && !psp?.label ? (
                    <div className="errors-wrapper">
                      <img src={WARNING_ICON} alt="warning-icon" />
                      <p className="error">Select Payment Provider</p>
                    </div>
                  ) : null}
                </div>
              </div>
            ) : (
              ''
            )}
          </div>
          <button
            type="submit"
            className={styles.registerButton}
            style={{
              backgroundColor: clientConfig.primaryButtonColor,
              color: clientConfig.onPrimaryButton,
            }}
          >
            Register
          </button>
          <p
            className={styles.loginLinkWrapper}
            style={{
              color: clientConfig.onBackground,
            }}
          >
            Already have an account?
            <Link href={`/${clientName || ''}`} legacyBehavior>
              {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
              <a
                style={{
                  color: clientConfig.linkColor,
                  marginLeft: '5px',
                }}
              >
                Login
              </a>
            </Link>
          </p>
        </form>
      </div>

      {isLoading && <Loader />}
    </>
  );
};

export default Register;
